import { action, computed, makeObservable, observable } from 'mobx';
import { SolgariApiService } from '../services/solgari-api/solgari-api.service';
import { Dynamics } from '../services/dynamics/dynamics';
import { IChatEntity } from '../shared/interfaces/chat-entity.interface';
import { COMMON } from '../configs/main.config';
import { IChatDetails } from '../shared/interfaces/chat-details.interface';
import { IInboxFilter } from '../shared/interfaces/inbox-filter.interface';
import { chatFilter } from '../shared/helpers/chat-filter.helper';
import { chatSorter } from '../shared/helpers/chat-sorter.helper';
import { EChannelProvider } from '../services/solgari-api/enums/channel-provider.enum';
import { EChatState } from '../services/solgari-api/enums/chat-state.enum';
import {
  HistoryType,
  IHistoryItem,
} from '../shared/interfaces/history-item.interface';
import { IPhoneCall } from '../shared/interfaces/phone-call.interface';
import { IHistoryFilter } from '../shared/interfaces/history-filter.interface';
import { historyFilter } from '../shared/helpers/history-filter.helper';
import { historySorter } from '../shared/helpers/history-sorter.helper';
import { ICrmChatNote } from '../shared/interfaces/crm-chat-note.interface';
import { IEntity } from '../shared/interfaces/entity.interface';
import { clearIdentity } from '../shared/helpers/clear-identity.helper';
import { RootStore } from './RootStore';

class ChatsStore {
  @observable
  historyItems: IHistoryItem[];

  @observable
  chats: IChatEntity[];

  @observable
  inboxFilter: IInboxFilter;

  @observable
  historyFilter: IHistoryFilter;

  @observable
  chatDetails: IChatDetails;

  @observable
  globalSpinner: boolean;

  @computed
  get unstoppedCount(): number {
    if (!this.chats) {
      return null;
    }

    return this.chats.filter((chat) => chat.state !== EChatState.STOPPED)
      ?.length;
  }

  @computed
  get inboxChats(): IChatEntity[] {
    if (!this.chats) {
      return [];
    }

    return (
      this.chats
        // Rule 1: there should be only NOT stopped chats
        .filter((chat) => chat.state !== EChatState.STOPPED)
        // Rule 2: form filters
        .filter((chat) => chatFilter(chat, this.inboxFilter))
        // Rule 3: form sorter
        .sort((chatA, chatB) => chatSorter(chatA, chatB, this.inboxFilter))
    );
  }

  @computed
  get history(): IHistoryItem[] {
    if (!this.historyItems?.length) {
      return [];
    }

    return (
      this.historyItems
        // Rule 1: form filters
        .filter((history) => historyFilter(history, this.historyFilter))
        // Rule 2: form sorter
        .sort((historyA, historyB) =>
          historySorter(historyA, historyB, this.historyFilter),
        )
    );
  }

  @action
  setGlobalSpinner = (value: boolean) => {
    this.globalSpinner = value;
  };

  @action
  setInboxFilter = (filter: Partial<IInboxFilter>) => {
    this.inboxFilter = {
      ...this.inboxFilter,
      ...filter,
    };
  };

  @action
  setHistoryFilter = (filter: Partial<IHistoryFilter>) => {
    this.historyFilter = {
      ...this.historyFilter,
      ...filter,
    };
  };

  @action
  getHistoryItemsFromApi = async () => {
    const [calls, missedCalls] = await Promise.all([
      Dynamics.getPhoneCalls(),
      SolgariApiService.getMissesCalls(),
    ]);
    const stoppedChats = (this.chats ?? []).filter(
      (chat) => chat.state === EChatState.STOPPED,
    );

    const mappedData: IHistoryItem[] = [
      ...stoppedChats,
      ...calls,
      ...missedCalls,
    ]
      .map((item: IChatEntity | IPhoneCall) => {
        if ((item as IChatEntity).chatId) {
          return {
            id: (item as IChatEntity).chatId,
            type: 'CHAT' as HistoryType,
            item: item as IChatEntity,
            createAt: new Date((item as IChatEntity).createdTimestamp),
          };
        }

        return {
          id: (item as IPhoneCall).id,
          type: 'PHONE' as HistoryType,
          item: item as IPhoneCall,
          createAt: (item as IPhoneCall).date,
        };
      })
      .sort((itemA, itemB) => {
        const a = itemA.createAt as Date;
        const b = itemB.createAt as Date;

        return new Date(b).getTime() - new Date(a).getTime();
      });

    this.historyItems = mappedData;
  };

  @action
  resetChatDetails = () => {
    this.chatDetails = null;
  };

  @action
  getChatDetailsFromApi = async (chatId: string) => {
    const messages = await SolgariApiService.getChatDetails(chatId);
    const localChat = this.chats.find((chat) => chat.chatId === chatId);

    const result = {
      ...localChat,
      messages,
    };

    this.chatDetails = result;
  };

  @action
  addChatLocally = (chatEntity: IChatEntity) => {
    this.chats = [...this.chats, chatEntity];
  };

  @action
  addChat = async ({
    chatId,
    chatNote,
    user,
  }: {
    chatId: string;
    chatNote?: ICrmChatNote;
    user: IEntity;
  }) => {
    const { chats } = await SolgariApiService.getChats();
    const foundChat = chats.find((c) => c.chatId === chatId);
    const chatEntity: IChatEntity = {
      ...foundChat,
      user,
      chatNote,
    };

    const filteredChats = this.chats.filter((c) => c.chatId !== chatId);
    this.chats = [...filteredChats, chatEntity];
    return chatEntity;
  };

  @action
  updateChat = async (chat: IChatEntity | IChatDetails) => {
    const { chats } = await SolgariApiService.getChats();
    const foundChat = chats.find((c) => c.chatId === chat.chatId);
    const chatEntity: IChatEntity = {
      ...foundChat,
      user: chat.user,
      chatNote: chat.chatNote,
    };

    const filteredChats = this.chats.filter((c) => c.chatId !== chat.chatId);
    this.chats = [...filteredChats, chatEntity];
  };

  @action
  updateEntities = async (entities: IEntity[]) => {
    this.chats = this.chats.map((chat) => {
      const foundEntity =
        entities.find((e) => {
          const phones = e?.phones ?? [];
          const cleanIdentity = clearIdentity(
            chat.channelProvider,
            chat.externalCustomerIdentity,
          );

          if (chat.channelProvider === EChannelProvider.SMS) {
            return phones.some((e) => e.includes(cleanIdentity));
          }

          if (chat.channelProvider === EChannelProvider.OUTLOOK) {
            return e.emails.includes(chat?.externalCustomerIdentity);
          }

          if (chat.channelProvider === EChannelProvider.WHATSAPP) {
            return phones.some((e) => e.includes(cleanIdentity));
          }

          return false;
        }) ?? COMMON.UNKNOWN_ENTITY;

      return {
        ...chat,
        user: foundEntity,
        chatNote: chat.chatNote,
      };
    });
  };

  @action
  getChatsFromApi = async () => {
    const [chats, chatNotes] = await Promise.all([
      SolgariApiService.getChats(),
      Dynamics.searchChatNotes({}),
    ]);

    const chatEntities = await Promise.all(
      chats.chats.map(async (chat) => {
        const foundChatNote = chatNotes.find(
          (note) => note.solgari_solgariconversationid === chat.chatId,
        );
        const cleanIdentity = clearIdentity(
          chat.channelProvider,
          chat.externalCustomerIdentity,
        );
        const matchedD365Record = await Dynamics.searchEntities(
          cleanIdentity,
          true,
          true,
        );
        const foundEntity = matchedD365Record[0] ?? COMMON.UNKNOWN_ENTITY;
        return {
          ...chat,
          user: foundEntity,
          chatNote: foundChatNote,
        };
      }),
    );

    this.chats = chatEntities;
  };

  constructor(public root: RootStore) {
    this.globalSpinner = false;

    this.inboxFilter = {
      dateRange: {
        fromDate: null,
        toDate: null,
      },
      orderBy: {
        direction: 'asc',
        field: 'createdTimestamp',
      },
      searchTerm: '',
      chatExceptions: [],
    };

    this.historyFilter = {
      dateRange: {
        fromDate: null,
        toDate: null,
      },
      orderBy: {
        direction: 'asc',
        field: 'createAt',
      },
      searchTerm: '',
      exceptions: [],
    };

    makeObservable(this);
  }
}

export default ChatsStore;
